import { useEffect, useRef, useState } from "react";
import type { Coordinate } from "@/entities/tracking";
import { loadGoogleMaps } from "@/shared/geo/googleMapsLoader";
import { mapStyleForTheme } from "@/shared/geo/mapTheme";
import { resolveTheme, usePreferencesStore } from "@/shared/stores/preferencesStore";
import { MapUnavailable } from "@/shared/ui";

type RecenterCommand = { coord: Coordinate; zoom?: number; nonce: number };

type Props = {
  initialCenter: Coordinate;
  /**
   * Appelé quand la carte s'arrête de bouger : coordonnées du CENTRE, c'est-à-dire
   * la position du pin fixe affiché par-dessus (overlay géré par l'écran).
   */
  onCenterChange: (coord: Coordinate) => void;
  /**
   * Recentrage programmatique (recherche / « ma position »). `nonce` rejoue la
   * commande même si les coordonnées sont identiques.
   */
  recenter?: RecenterCommand | null;
  onFatalError?: () => void;
};

const DEFAULT_ZOOM = 16; // ~ l'équivalent du DEFAULT_DELTA 0.012 de react-native-maps

/**
 * Sélecteur d'emplacement — pin central fixe, la carte glisse dessous.
 *
 * Remplace les DEUX implémentations natives : `GooglePickerMap`
 * (react-native-maps, qui exigeait un dev build) et `LeafletPickerMap` (Leaflet
 * dans une WebView). Une seule suffit désormais.
 *
 * Le pin n'est PAS un marqueur de la carte : c'est un overlay posé par l'écran
 * appelant, immobile au centre. C'est ce qui donne la sensation « la carte
 * bouge sous le pin » de Gozem/Yango, impossible à obtenir avec un marqueur
 * qu'il faudrait repositionner à chaque frame.
 *
 * ## Fournisseur : Google Maps
 * Les tuiles OpenStreetMap ont été remplacées par le SDK Google Maps, dont le
 * projet utilise déjà la Routes API pour les tracés d'itinéraire : le fond de
 * carte et la route décrivent enfin le même réseau routier.
 *
 * ⚠️ Exige `VITE_GOOGLE_MAPS_API_KEY` avec **Maps JavaScript API** activée — la
 * Routes API seule ne suffit pas, ce sont deux services distincts. La clé doit
 * être de type NAVIGATEUR, restreinte par référent HTTP ; celle du
 * `google-services.json` est liée au package Android et sera refusée.
 */
export function PickerMap({ initialCenter, onCenterChange, recenter, onFatalError }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [failed, setFailed] = useState(false);

  // `onCenterChange` change d'identité à chaque rendu du parent : on le lit via
  // une ref pour ne pas réabonner le listener (et donc ne pas recréer la carte).
  const onCenterChangeRef = useRef(onCenterChange);
  onCenterChangeRef.current = onCenterChange;

  const onFatalErrorRef = useRef(onFatalError);
  onFatalErrorRef.current = onFatalError;

  /**
   * Recentrage demandé AVANT que la carte n'existe.
   *
   * Le SDK Google arrive de façon asynchrone. Une commande émise entre-temps —
   * typiquement une adresse choisie dans la liste de recherche, juste après
   * l'ouverture — trouvait `mapRef.current` à `null` et était PERDUE, sans
   * trace. L'écran affichait alors la nouvelle adresse sur une carte restée au
   * point précédent : le pin, donc le point confirmé, ne correspondait plus au
   * libellé lu par l'utilisateur.
   *
   * On la garde ici pour la rejouer dès que la carte est prête.
   */
  const pendingRecenterRef = useRef<RecenterCommand | null>(null);

  /** Applique un recentrage, ou le met en attente si la carte n'est pas prête. */
  const applyRecenter = useRef((command: RecenterCommand) => {
    const map = mapRef.current;
    if (!map) {
      pendingRecenterRef.current = command;
      return;
    }
    map.panTo({ lat: command.coord.latitude, lng: command.coord.longitude });
    map.setZoom(command.zoom ?? DEFAULT_ZOOM);
  });

  useEffect(() => {
    let cancelled = false;

    void loadGoogleMaps()
      .then((maps) => {
        const el = containerRef.current;
        // Le SDK arrive de façon asynchrone : l'écran a pu être quitté
        // entre-temps, et la carte n'aurait plus de conteneur où se peindre.
        if (cancelled || !el || mapRef.current) return;

        const map = new maps.Map(el, {
          // Tuiles assombries en thème sombre : sans style, Google rend une
          // carte blanche éclatante au milieu d'une interface sombre.
          styles: mapStyleForTheme(resolveTheme(usePreferencesStore.getState().theme)),
          center: { lat: initialCenter.latitude, lng: initialCenter.longitude },
          zoom: DEFAULT_ZOOM,
          disableDefaultUI: true,
          gestureHandling: "greedy", // un doigt suffit : pas de « Ctrl + molette »
          clickableIcons: false, // sinon un POI ouvre sa fiche au lieu de déplacer le pin
        });

        // `idle` et non `center_changed` : on ne remonte le centre qu'à l'ARRÊT
        // du geste. À chaque frame, un reverse-geocoding partirait des dizaines
        // de fois pour un seul déplacement.
        map.addListener("idle", () => {
          const c = map.getCenter();
          if (!c) return;
          onCenterChangeRef.current({ latitude: c.lat(), longitude: c.lng() });
        });

        mapRef.current = map;

        // Une commande reçue pendant le chargement attendait son tour : sans
        // ce rejeu, elle serait définitivement perdue et la carte resterait au
        // point de départ pendant que l'écran annonce une autre adresse.
        const pending = pendingRecenterRef.current;
        if (pending) {
          pendingRecenterRef.current = null;
          applyRecenter.current(pending);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        console.error("[PickerMap] initialisation Google Maps échouée", e);
        setFailed(true);
        onFatalErrorRef.current?.();
      });

    return () => {
      cancelled = true;
      // Le SDK n'expose pas de `destroy()` : on relâche la référence, le
      // conteneur DOM disparaît avec le composant.
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!recenter) return;
    applyRecenter.current(recenter);
    // `nonce` en dépendance : c'est lui qui rejoue une commande identique.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenter?.nonce]);

  if (failed) return <MapUnavailable />;

  // Fond visible le temps que les tuiles arrivent : un token, pas un vert en
  // dur — sinon il flashe en clair sur une interface sombre.
  return <div ref={containerRef} className="h-full w-full bg-gray-100" />;
}
